import * as React from 'react';
import styled, { css } from 'styled-components';

import IcoError from '@floji-internal/mui-icons/Cancel';
import IcoWarn from '@floji-internal/mui-icons/Warning';
import { Errors } from '--root/Errors';
import { Label } from '../Label/Label';

// TODO:    Try to use same pattern as the toggle switch
//          May want to clear error state when onChange fires and await further validation

type TextInputStyleProps = {
    state?: "default" | "warning" | "error" | "disabled"
    size?: "small" | "medium" | "large"
    width?: number | string
    rounded?: boolean
    align?: "center" | "left" | "right";
    resetMargins?: boolean;
}

export const StyledInputContainer = styled.div<TextInputStyleProps>` 
    display:flex;
    flex-direction:column;
    text-align: left;
    width: ${props => props.width ?
        typeof props.width == "number" ?
            props.width + 'px' :
            props.width :
        'initial'};
    ${props => props.resetMargins && css`
        margin-left: ${props.theme.spacing(-1)};
        margin-right: ${props.theme.spacing(-1)};
    `}; 
`

export const StyledInputWrapper = styled.div<TextInputStyleProps>`
    position: relative;
    display: flex;
    align-items: stretch;
    width: 100%;
    min-height: ${props => {
        switch (props.size) {
            case "small": return "32px"
            case "large": return "56px"
            default: return "40px;"
        }
    }};
    box-sizing: border-box;
    padding:0;
    margin:0;
    border: 1px solid ${props =>
        props.state == "error" ?
            props.theme.palette.danger.main :
            props.state == "warning" ?
                props.theme.palette.warning.main :
                props.theme.palette.ui.border
    };
    border-radius: ${props => props.rounded ? props.theme.radius.large : props.theme.radius.small};
    box-shadow: ${props => props.theme.shadows.inputInset};

    :focus-within {
        border-color: ${props => props.theme.palette.primary.main};
    }
    > input {
        ${props => props.size == "small" ? props.theme.typography.body2 : props.theme.typography.body1};
        color: ${props =>
        props.state == "error" ?
            props.theme.palette.danger.main :
            props.theme.palette.text.primary
    };
        flex: 1 1 auto;
        min-width: 0; // Needed as browser is giving the input a default width
        display: block;
        border: none;
        outline: none;
        background: transparent;
        margin: 0;
        padding: 0;
        text-align: ${props => props.align || "left"};
        ::placeholder {
            color:${props => props.theme.palette.text.disabled};
        }
    }

    input[type="search"]::-webkit-search-decoration,
    input[type="search"]::-webkit-search-cancel-button,
    input[type="search"]::-webkit-search-results-button,
    input[type="search"]::-webkit-search-results-decoration {
        display: none;
    }

    > input:first-child {
        padding-left: ${props => props.theme.spacing(props.size === "small" ? 1 : 2)};
    }

    > input:last-child {
        padding-right: ${props => props.theme.spacing(props.size === "small" ? 1 : 2)};
    }
`;

const StartEndComponent = styled.div<TextInputStyleProps>`
    display: flex;
    align-items: center;
    margin: ${props => props.theme.spacing(0, props.size == "small" ? 0.5 : 1)};

    > svg {
        align-self: center;
        color: ${props => props.state == "disabled" ?
        props.theme.palette.text.disabled :
        props.state == "error" ?
            props.theme.palette.danger.main :
            props.state == "warning" ?
                props.theme.palette.warning.main :
                props.theme.palette.text.secondary
    };
    }
`;

const HelperMessage = styled.div<TextInputStyleProps>`
    /** Typography */
    ${props => props.theme.typography.body3}
    color: ${props =>
        props.state == "disabled" ?
            props.theme.palette.text.disabled :
            props.state == "error" ?
                props.theme.palette.danger.main :
                props.theme.palette.text.secondary
    };
    margin: 4px 0 0 0;
    padding: ${props => props.theme.spacing(0, 1)};
`

type TextInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "onChange" | "width"> & {
    id?: string,
    optional?: boolean
    label?: React.ReactNode | string
    message?: string | Errors.FormFieldError // Error and warning props can be ignored if passing an ErrorMessage object
    error?: boolean
    warning?: boolean
    containerRef?: ((instance: HTMLDivElement | null) => void) | React.RefObject<HTMLDivElement> | null | undefined,
    inputRef?: ((instance: HTMLInputElement | null) => void) | React.RefObject<HTMLInputElement> | null | undefined,
    onChange?: (e: React.ChangeEvent<HTMLInputElement>, value: string) => void
    leadingComponent?: React.ReactElement
    trailingComponent?: React.ReactElement
    size?: "small" | "medium" | "large"
    width?: string | number
    rounded?: boolean
    align?: "center" | "left" | "right"
    resetMargins?: boolean
}


const TextInput: React.FC<TextInputProps> = props => {
    const {
        id, label, message, error, warning, optional, containerRef, inputRef,
        leadingComponent: startIcon, trailingComponent: endIcon, size, width, rounded, disabled, align,
        children, onChange: onChangeProp, value, resetMargins,
        ...inputProps
    } = props;

    // Work out what state to render the field in and the message to render
    const errorTest = error || (typeof message == "object" && message.level == "error");
    const warnTest = warning || (typeof message == "object" && message.level == "warning");
    const fieldState = disabled ? "disabled" : errorTest ? "error" : warnTest ? "warning" : undefined;
    const fieldStateIcon = fieldState == "error" ? <IcoError /> : fieldState == "warning" ? <IcoWarn /> : undefined;
    const fieldMessage = typeof message == "object" ? message.message : message;

    // Keep track of the current value
    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChangeProp && onChangeProp(e, e.target.value);
    }

    return (
        <StyledInputContainer ref={containerRef} className={props.className} width={width} resetMargins={resetMargins}>
            {label && <Label htmlFor={id} disabled={fieldState === "disabled"} optional={optional}>
                {props.label}
            </Label>}
            <StyledInputWrapper rounded={rounded} state={fieldState} size={size} align={align}>
                {startIcon && <StartEndComponent size={size} state={fieldState}>{startIcon}</StartEndComponent>}
                <input id={id} disabled={disabled} onInput={handleInput} value={value} {...inputProps} ref={inputRef} />
                {endIcon && <StartEndComponent size={size} state={fieldState}>{endIcon || fieldStateIcon}</StartEndComponent>}
            </StyledInputWrapper>
            {fieldMessage && <HelperMessage state={fieldState}>{fieldMessage}</HelperMessage>}
        </StyledInputContainer>
    )
}

export { TextInput, TextInputProps };
